import { createFileRoute, Link, Outlet, useRouterState, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { getCurrentUserRole } from "@/lib/admin.functions";
import { commentModerationCounts } from "@/lib/comments.functions";
import { cn } from "@/lib/utils";
import {
  Shield,
  Users,
  RefreshCw,
  ListChecks,
  Menu,
  ScrollText,
  MessageCircle,
  Activity,
  PlusCircle,
  BarChart3,
  SlidersHorizontal,
  Link2,
  KeyRound,
  MailCheck,
  FileClock,
  UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SyncAlerts } from "@/components/sync-alerts";

const ROLE_QUERY = {
  queryKey: ["role-check"],
  queryFn: () => getCurrentUserRole(),
  staleTime: 5 * 60_000,
  retry: 1,
} as const;

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [{ title: "Admin · Beit Tefilah Rechovot" }, { name: "robots", content: "noindex" }],
  }),
  // Confere a permissão antes de renderizar: evita a tela intermediária de
  // carregamento e o deslocamento do conteúdo depois dela.
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(ROLE_QUERY).catch(() => null);
  },
  component: AdminLayout,
});

const ADMIN_NAV: {
  to:
    | "/admin"
    | "/admin/painel"
    | "/admin/sincronizar"
    | "/admin/sync-status"
    | "/admin/ensinos"
    | "/admin/adicionar"
    | "/admin/membros"
    | "/admin/aprovacoes"
    | "/admin/logs"
    | "/admin/comentarios"
    | "/admin/regras"
    | "/admin/integracao"
    | "/admin/integracao-logs"
    | "/admin/youtube"
    | "/admin/email-dns";
  label: string;
  icon: any;
  exact?: boolean;
}[] = [
  { to: "/admin", label: "Visão geral", icon: Shield, exact: true },
  { to: "/admin/painel", label: "Painel de engajamento", icon: BarChart3 },
  { to: "/admin/comentarios", label: "Comentários", icon: MessageCircle },
  { to: "/admin/sincronizar", label: "Sincronizar YouTube", icon: RefreshCw },
  { to: "/admin/sync-status", label: "Status de syncs", icon: Activity },
  { to: "/admin/regras", label: "Regras de importação", icon: SlidersHorizontal },
  { to: "/admin/ensinos", label: "Ensinos", icon: ListChecks },
  { to: "/admin/adicionar", label: "Adicionar vídeo", icon: PlusCircle },
  { to: "/admin/aprovacoes", label: "Aprovações de cadastro", icon: UserCheck },
  { to: "/admin/membros", label: "Membros", icon: Users },
  { to: "/admin/integracao", label: "Integração de cadastros", icon: Link2 },
  { to: "/admin/youtube", label: "Chave do YouTube", icon: KeyRound },
  { to: "/admin/email-dns", label: "Entregabilidade de e-mail", icon: MailCheck },
  { to: "/admin/integracao-logs", label: "Logs de integração", icon: FileClock },
  { to: "/admin/logs", label: "Logs de atividade", icon: ScrollText },
];

function AdminLayout() {
  const navigate = useNavigate();
  const fn = useServerFn(getCurrentUserRole);
  const commentsCountFn = useServerFn(commentModerationCounts);
  const { data, isLoading, isError } = useQuery({
    ...ROLE_QUERY,
    queryFn: () => fn(),
  });
  const { data: pendingComments } = useQuery({
    queryKey: ["pending-comments-count"],
    queryFn: () => commentsCountFn(),
    enabled: !!data?.isAdmin,
    refetchInterval: 30_000,
  });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && data && !data.isAdmin) navigate({ to: "/inicio", replace: true });
  }, [isLoading, data, navigate]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  // Falha na verificação de permissão não deve deixar a tela presa no esqueleto.
  if (isError) {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-6 text-sm text-destructive">
        Não foi possível verificar suas permissões de administrador. Atualize a página e tente
        novamente.
      </div>
    );
  }

  if (isLoading || !data?.isAdmin) {
    return <div className="min-h-[70vh] animate-pulse rounded-xl bg-secondary" aria-hidden />;
  }

  const NavList = (
    <nav className="space-y-1">
      {ADMIN_NAV.map(({ to, label, icon: Icon, exact }) => {
        const active = exact ? pathname === to : pathname.startsWith(to);
        const badge =
          to === "/admin/comentarios" && pendingComments && pendingComments.pending > 0
            ? pendingComments.pending
            : null;
        return (
          <Link
            key={to}
            to={to}
            className={cn(
              "flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              active
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-secondary hover:text-primary",
            )}
          >
            <span className="flex items-center gap-2">
              <Icon className="h-4 w-4" />
              {label}
            </span>
            {badge && (
              <span
                className={cn(
                  "flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold",
                  active ? "bg-primary-foreground text-primary" : "bg-gold text-gold-foreground",
                )}
              >
                {badge}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[220px_1fr] lg:gap-8">
      <div className="lg:hidden">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMobileNavOpen((v) => !v)}
          className="w-full justify-start"
        >
          <Menu className="mr-2 h-4 w-4" /> Menu do admin
        </Button>
        {mobileNavOpen && (
          <div className="mt-2 rounded-lg border border-border bg-card p-2">{NavList}</div>
        )}
      </div>
      <aside className="hidden lg:block">{NavList}</aside>
      <div className="min-w-0">
        <SyncAlerts />
        <Outlet />
      </div>
    </div>
  );
}
